import { memo, useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { type NodeProps, Handle, Position } from '@xyflow/react';
import { Check, X, ShoppingBag } from 'lucide-react';
import { AREA_COLORS } from '@/components/tracker/data/area-colors';
import { ENTRANCE_CATEGORY_COLORS, ZONE_TO_COLOR_REGION } from '../data/entrance-types';
import type { ColorRegion, EntranceCategory, EntranceConnection, ShopItem } from '../data/entrance-types';
import { DestinationPicker } from '../shared/destination-picker';
import { ZoneRewardPicker } from '../shared/zone-reward-picker';
import { ZoneRewardDisplay } from '../shared/zone-reward-display';
import { ShopEditorModal, getShopItemCount } from '../shared/shop-editor-modal';
import { ScrubsCounterModal } from '../shared/scrubs-counter-modal';
import { ALL_VANILLA_ENTRANCES } from '../data/vanilla-entrances';

// Dead-end quick destinations (right-click menu)
type DeadEndType = 'interior' | 'grotto' | 'boss' | 'navigation' | 'done';

interface DeadEndEntry { label: string; value: string; type: DeadEndType }

const DEAD_END_GROTTOS: DeadEndEntry[] = [
  { label: 'Generic Grotto', value: 'Generic Grotto', type: 'grotto' },
  { label: 'Scrubs', value: 'Scrubs Grotto', type: 'grotto' },
  { label: 'Sun Grave', value: 'Sun Song Grave', type: 'grotto' },
  { label: 'Dampe Race', value: 'Dampe Race', type: 'grotto' },
  { label: 'Royal Family Tomb', value: 'Royal Family Tomb', type: 'grotto' },
  { label: 'Cow', value: 'Cow Grotto', type: 'grotto' },
];

const DEAD_END_INTERIORS: DeadEndEntry[] = [
  { label: 'Great Fairy', value: 'Great Fairy Fountain', type: 'interior' },
  { label: 'Lab', value: 'LH Lab', type: 'interior' },
  { label: 'Windmill', value: 'Kak Windmill', type: 'interior' },
  { label: 'Shop', value: 'Shop', type: 'interior' },
  { label: 'Ranch Tower', value: 'LLR Tower', type: 'interior' },
  { label: 'Talon', value: 'LLR Talons House', type: 'interior' },
  { label: 'Archery Child', value: 'Market Shooting Gallery', type: 'interior' },
  { label: 'Archery Adult', value: 'Kak Shooting Gallery', type: 'interior' },
  { label: 'Skull House', value: 'Kak House of Skulltula', type: 'interior' },
  { label: 'Bowling', value: 'Market Bombchu Bowling', type: 'interior' },
  { label: 'CMG', value: 'Market Treasure Chest Game', type: 'interior' },
  { label: 'ATZ', value: 'ATZ', type: 'interior' },
  { label: 'Skull', value: 'Skull Kid', type: 'interior' },
  { label: 'Mask Shop', value: 'Market Mask Shop', type: 'interior' },
  { label: 'Fishing', value: 'LH Fishing Hole', type: 'interior' },
  { label: 'ToT', value: 'Temple of Time', type: 'interior' },
];

const DEAD_END_NAVIGATION: DeadEndEntry[] = [
  // Potion Shop
  { label: 'Potion Front', value: 'Potion Shop Front', type: 'navigation' },
  { label: 'Potion Back', value: 'Potion Shop Back', type: 'navigation' },
  // Market Bridge
  { label: 'Bridge => HF', value: 'Market Bridge HF', type: 'navigation' },
  { label: 'Bridge => Market', value: 'Market Bridge Market', type: 'navigation' },
  // LW Bridge
  { label: 'LW Bridge => KF', value: 'LW Bridge KF', type: 'navigation' },
  { label: 'LW Bridge => HF', value: 'LW Bridge HF', type: 'navigation' },
];

const DEAD_END_BOSSES: DeadEndEntry[] = [
  { label: 'Queen Gohma', value: 'Queen Gohma Boss Room', type: 'boss' },
  { label: 'King Dodongo', value: 'King Dodongo Boss Room', type: 'boss' },
  { label: 'Barinade', value: 'Barinade Boss Room', type: 'boss' },
  { label: 'Phantom Ganon', value: 'Phantom Ganon Boss Room', type: 'boss' },
  { label: 'Volvagia', value: 'Volvagia Boss Room', type: 'boss' },
  { label: 'Morpha', value: 'Morpha Boss Room', type: 'boss' },
  { label: 'Bongo Bongo', value: 'Bongo Bongo Boss Room', type: 'boss' },
  { label: 'Twinrova', value: 'Twinrova Boss Room', type: 'boss' },
  { label: 'Ganons Tower', value: 'Ganons Castle Tower', type: 'boss' },
];

// All dead-end entries for lookups
const ALL_DEAD_ENDS: DeadEndEntry[] = [
  ...DEAD_END_GROTTOS, ...DEAD_END_INTERIORS, ...DEAD_END_NAVIGATION, ...DEAD_END_BOSSES,
];

// Destination color logic
const DEAD_END_INTERIOR_COLOR = '#3b82f6'; // blue
const DEAD_END_GROTTO_COLOR = '#a16207';   // amber/brown
const DEAD_END_BOSS_COLOR = '#a855f7';     // purple (matches boss category)
const DEAD_END_NAV_COLOR = '#22c55e';      // green (matches overworld category)
const DEAD_END_DONE_COLOR = '#ef4444';     // red

// Value → short label map for display
const DEAD_END_DISPLAY: Map<string, string> = new Map(ALL_DEAD_ENDS.map(d => [d.value, d.label]));

export const DEAD_END_VALUES = new Set(ALL_DEAD_ENDS.map(d => d.value));
DEAD_END_VALUES.add('✓ Done');

// Build lookup: vanillaTo -> zone for color resolution
const VANILLA_TO_ZONE = new Map<string, string>();
for (const ent of ALL_VANILLA_ENTRANCES) {
  VANILLA_TO_ZONE.set(ent.vanillaTo, ent.zone);
}

/** Display label for a tracked destination. Uses short label for dead-ends, actualFrom for links. */
function getDestinationLabel(destination: string, from: string | null): string {
  if (destination === '✓ Done') return '✓ Done';
  // Dead-end: show short label instead of full value
  const shortLabel = DEAD_END_DISPLAY.get(destination);
  if (shortLabel) return shortLabel;
  // If we have a from label (set by Ctrl+Click), use it
  if (from) return from;
  return destination;
}

const DEAD_END_TYPE_COLORS: Record<DeadEndType, string> = {
  interior: DEAD_END_INTERIOR_COLOR,
  grotto: DEAD_END_GROTTO_COLOR,
  boss: DEAD_END_BOSS_COLOR,
  navigation: DEAD_END_NAV_COLOR,
  done: DEAD_END_DONE_COLOR,
};

function getDestinationColor(destination: string | null, from: string | null = null): string {
  if (!destination) return '#6b7280';
  if (destination === '✓ Done') return DEAD_END_DONE_COLOR;

  // Linked entrance (Ctrl+Click): use the destination zone color, not dead-end type
  if (from) {
    const targetZone = VANILLA_TO_ZONE.get(destination);
    if (targetZone) {
      const region = ZONE_TO_COLOR_REGION[targetZone];
      if (region) return AREA_COLORS[region] ?? '#22c55e';
    }
  }

  // Check if it's a dead-end (right-click menu)
  const deadEnd = ALL_DEAD_ENDS.find(d => d.value === destination);
  if (deadEnd) return DEAD_END_TYPE_COLORS[deadEnd.type];

  // Navigation destination: find the zone and use its region color
  const targetZone = VANILLA_TO_ZONE.get(destination);
  if (targetZone) {
    const region = ZONE_TO_COLOR_REGION[targetZone];
    if (region) return AREA_COLORS[region] ?? '#22c55e';
  }

  return '#22c55e'; // fallback green
}

export interface ZoneNodeData {
  label: string;
  zone: string;
  colorRegion: ColorRegion;
  entrances: EntranceConnection[];
  trackedCount: number;
  totalCount: number;
  onTrack: (id: string, destination: string, from?: string | null, linkedTo?: string | null, isReverse?: boolean) => void;
  onUntrack: (id: string) => void;
  linkSourceId: string | null;
  onEntranceClick: (entranceId: string, vanillaTo: string, zone: string, label: string) => void;
  zoneRewards: Record<string, string[]>;
  onToggleZoneReward: (zone: string, rewardId: string) => void;
  shopData: Record<string, ShopItem[]>;
  scrubsCount: Record<string, number>;
  onSetShopData: (entranceId: string, items: ShopItem[]) => void;
  onSetScrubsCount: (entranceId: string, count: number) => void;
  [key: string]: unknown;
}

function ZoneNodeComponent({ data }: NodeProps) {
  const {
    label, zone, colorRegion, entrances, trackedCount, totalCount,
    onTrack, onUntrack, linkSourceId, onEntranceClick,
    zoneRewards, onToggleZoneReward,
    shopData, scrubsCount, onSetShopData, onSetScrubsCount,
  } = data as unknown as ZoneNodeData;
  const color = AREA_COLORS[colorRegion] ?? '#6b7280';
  const [pickerFor, setPickerFor] = useState<string | null>(null);
  const [showRewardPicker, setShowRewardPicker] = useState(false);
  const headerRef = useRef<HTMLDivElement>(null);
  const [headerRect, setHeaderRect] = useState<{ top: number; left: number; bottom: number } | null>(null);

  useEffect(() => {
    if (linkSourceId) setPickerFor(null);
  }, [linkSourceId]);

  const handleHeaderClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (headerRef.current) {
      const rect = headerRef.current.getBoundingClientRect();
      setHeaderRect({ top: rect.top, left: rect.left, bottom: rect.bottom });
    }
    setShowRewardPicker((prev) => !prev);
  };

  const currentRewards = zoneRewards[zone] ?? [];

  return (
    <div
      className="rounded-lg border-2 w-[220px]"
      style={{ borderColor: color, backgroundColor: `${color}10` }}
    >
      <div
        ref={headerRef}
        className="px-2 py-1 rounded-t-[5px] text-[11px] font-bold text-white flex items-center justify-between cursor-pointer hover:brightness-110"
        style={{ backgroundColor: color }}
        onClick={handleHeaderClick}
      >
        <span className="truncate">{label}</span>
        <div className="flex items-center gap-1 flex-shrink-0">
          <ZoneRewardDisplay rewards={currentRewards} />
          <span className="text-[10px] opacity-80">
            {trackedCount}/{totalCount}
          </span>
        </div>
      </div>

      {showRewardPicker && headerRect && (
        <ZoneRewardPicker
          zone={zone}
          zoneRewards={zoneRewards}
          onToggle={onToggleZoneReward}
          onClose={() => setShowRewardPicker(false)}
          anchorRect={headerRect}
        />
      )}

      <div className="py-1">
        {entrances.map((conn) => (
          <EntranceRow
            key={conn.id}
            conn={conn}
            isPickerOpen={pickerFor === conn.id}
            onOpenPicker={() => setPickerFor(pickerFor === conn.id ? null : conn.id)}
            onClosePicker={() => setPickerFor(null)}
            onTrack={onTrack}
            onUntrack={onUntrack}
            isLinkSource={linkSourceId === conn.id}
            isLinking={linkSourceId !== null}
            onEntranceClick={onEntranceClick}
            shopItems={shopData[conn.id]}
            scrubsCount={scrubsCount[conn.id] ?? 0}
            onSetShopData={onSetShopData}
            onSetScrubsCount={onSetScrubsCount}
          />
        ))}
      </div>
    </div>
  );
}

interface EntranceRowProps {
  conn: EntranceConnection;
  isPickerOpen: boolean;
  onOpenPicker: () => void;
  onClosePicker: () => void;
  onTrack: (id: string, destination: string, from?: string | null, linkedTo?: string | null, isReverse?: boolean) => void;
  onUntrack: (id: string) => void;
  isLinkSource: boolean;
  isLinking: boolean;
  onEntranceClick: (entranceId: string, vanillaTo: string, zone: string, label: string) => void;
  shopItems?: ShopItem[];
  scrubsCount: number;
  onSetShopData: (entranceId: string, items: ShopItem[]) => void;
  onSetScrubsCount: (entranceId: string, count: number) => void;
}

function EntranceRow({
  conn, isPickerOpen, onOpenPicker, onClosePicker,
  onTrack, onUntrack,
  isLinkSource, isLinking, onEntranceClick,
  shopItems, scrubsCount, onSetShopData, onSetScrubsCount,
}: EntranceRowProps) {
  const catColor = ENTRANCE_CATEGORY_COLORS[conn.category as EntranceCategory];
  const rowRef = useRef<HTMLDivElement>(null);
  const pickerRef = useRef<HTMLDivElement>(null);
  const contextMenuRef = useRef<HTMLDivElement>(null);
  const [pickerPos, setPickerPos] = useState<{ top: number; left: number } | null>(null);
  const [contextMenu, setContextMenu] = useState<{ top: number; left: number } | null>(null);
  const [showShopEditor, setShowShopEditor] = useState(false);
  const [showScrubsCounter, setShowScrubsCounter] = useState(false);

  const isShopDeadEnd = conn.isTracked && conn.actualDestination === 'Shop';
  const isScrubsDeadEnd = conn.isTracked && conn.actualDestination === 'Scrubs Grotto';
  const shopItemCount = getShopItemCount(shopItems);

  // Compute portal position when picker opens
  useEffect(() => {
    if (isPickerOpen && rowRef.current) {
      const rect = rowRef.current.getBoundingClientRect();
      setPickerPos({ top: rect.top, left: rect.right + 4 });
    } else {
      setPickerPos(null);
    }
  }, [isPickerOpen]);

  // Close picker on outside click
  useEffect(() => {
    if (!isPickerOpen) return;
    const handler = (e: MouseEvent) => {
      if (
        pickerRef.current && !pickerRef.current.contains(e.target as Node) &&
        rowRef.current && !rowRef.current.contains(e.target as Node)
      ) {
        onClosePicker();
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [isPickerOpen, onClosePicker]);

  // Close context menu on outside click or Escape
  useEffect(() => {
    if (!contextMenu) return;
    const handleMouseDown = (e: MouseEvent) => {
      if (contextMenuRef.current && !contextMenuRef.current.contains(e.target as Node)) {
        setContextMenu(null);
      }
    };
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setContextMenu(null);
    };
    document.addEventListener('mousedown', handleMouseDown);
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('mousedown', handleMouseDown);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [contextMenu]);

  const handleClick = (e: React.MouseEvent) => {
    if (e.ctrlKey || e.metaKey) {
      e.stopPropagation();
      onEntranceClick(conn.id, conn.vanillaTo, conn.zone, conn.label);
    } else if (isLinking) {
      onEntranceClick(conn.id, conn.vanillaTo, conn.zone, conn.label);
    } else {
      onOpenPicker();
    }
  };

  const handleDoubleClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();
    if (isShopDeadEnd) {
      setShowShopEditor(true);
    } else if (isScrubsDeadEnd) {
      if (rowRef.current) {
        const rect = rowRef.current.getBoundingClientRect();
        setShowScrubsCounter(true);
        setPickerPos({ top: rect.top, left: rect.left });
      }
    }
  };

  const handleContextMenu = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setContextMenu({ top: e.clientY, left: e.clientX });
  };

  const selectDeadEnd = (value: string) => {
    onTrack(conn.id, value);
    setContextMenu(null);
  };

  const markDone = () => {
    onTrack(conn.id, '✓ Done');
    setContextMenu(null);
  };

  const clearEntrance = () => {
    onUntrack(conn.id);
    setContextMenu(null);
  };

  return (
    <div ref={rowRef} className="relative">
      <Handle type="source" position={Position.Right} id={`src-${conn.id}`}
        className="!bg-transparent !w-0 !h-0 !border-0 !min-w-0 !min-h-0 !right-0" />
      <Handle type="source" position={Position.Left} id={`src-left-${conn.id}`}
        className="!bg-transparent !w-0 !h-0 !border-0 !min-w-0 !min-h-0 !left-0" />
      <Handle type="target" position={Position.Left} id={`tgt-${conn.id}`}
        className="!bg-transparent !w-0 !h-0 !border-0 !min-w-0 !min-h-0 !left-0" />

      <div
        className={`
          mx-1 px-1.5 py-0.5 rounded text-[10px] cursor-pointer select-none
          flex items-center gap-1 transition-colors
          hover:bg-white/5
          ${conn.isTracked ? 'text-gray-200' : 'text-gray-500'}
          ${isLinkSource ? 'ring-1 ring-zelda-gold bg-zelda-gold/15 !text-zelda-gold' : ''}
          ${isLinking && !isLinkSource ? 'hover:!bg-zelda-gold/10 hover:ring-1 hover:ring-zelda-gold/50' : ''}
        `}
        onClick={handleClick}
        onDoubleClick={handleDoubleClick}
        onContextMenu={handleContextMenu}
      >
        <span
          className="w-1.5 h-1.5 rounded-full flex-shrink-0"
          style={{ backgroundColor: isLinkSource ? '#d4a017' : catColor }}
        />
        <span className="truncate flex-1 min-w-0">{conn.label}</span>
        {conn.isTracked && conn.actualDestination && (
          <>
            <span
              className="text-[9px] truncate max-w-[80px]"
              style={{ color: getDestinationColor(conn.actualDestination, conn.actualFrom) }}
              title={getDestinationLabel(conn.actualDestination, conn.actualFrom)}
            >
              → {getDestinationLabel(conn.actualDestination, conn.actualFrom)}
            </span>
            {/* Shop badge */}
            {isShopDeadEnd && (
              <span
                className="text-[8px] px-1 py-px rounded-full flex-shrink-0 flex items-center gap-0.5"
                style={{
                  backgroundColor: shopItemCount > 0 ? '#3b82f620' : '#6b728020',
                  color: shopItemCount > 0 ? '#60a5fa' : '#6b7280',
                  border: `1px solid ${shopItemCount > 0 ? '#3b82f640' : '#6b728040'}`,
                }}
                title="Double-click to edit shop items"
              >
                <ShoppingBag size={7} />
                {shopItemCount}/8
              </span>
            )}
            {/* Scrubs badge */}
            {isScrubsDeadEnd && (
              <span
                className="text-[8px] px-1 py-px rounded-full flex-shrink-0"
                style={{
                  backgroundColor: scrubsCount > 0 ? '#a1620720' : '#6b728020',
                  color: scrubsCount > 0 ? '#d97706' : '#6b7280',
                  border: `1px solid ${scrubsCount > 0 ? '#a1620740' : '#6b728040'}`,
                }}
                title="Double-click to edit scrubs count"
              >
                {scrubsCount}
              </span>
            )}
          </>
        )}
        {!conn.isTracked && !isLinkSource && (
          <span className="text-[9px] text-gray-700">?</span>
        )}
        {isLinkSource && (
          <span className="text-[9px] text-zelda-gold">source</span>
        )}
      </div>

      {/* Autocomplete picker (left click) */}
      {isPickerOpen && !isLinking && pickerPos && createPortal(
        <div
          ref={pickerRef}
          className="fixed"
          style={{ top: pickerPos.top, left: pickerPos.left, zIndex: 9999 }}
          onClick={(e) => e.stopPropagation()}
          onMouseDown={(e) => e.stopPropagation()}
          onPointerDown={(e) => e.stopPropagation()}
        >
          <DestinationPicker
            currentValue={conn.actualDestination}
            onSelect={(dest, from) => {
              onTrack(conn.id, dest, from);
              onClosePicker();
            }}
            onClear={() => {
              onUntrack(conn.id);
              onClosePicker();
            }}
            onClose={onClosePicker}
          />
        </div>,
        document.body,
      )}

      {/* Context menu (right click) — dead-end destinations, centered modal */}
      {contextMenu && createPortal(
        <div
          className="fixed inset-0 flex items-center justify-center"
          style={{ zIndex: 9999 }}
          onClick={(e) => { e.stopPropagation(); setContextMenu(null); }}
          onMouseDown={(e) => e.stopPropagation()}
          onPointerDown={(e) => e.stopPropagation()}
        >
          <div
            ref={contextMenuRef}
            className="bg-gray-900 border border-gray-700 rounded-lg shadow-xl py-1.5 px-1.5"
            style={{ width: '480px' }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Done / Clear — top */}
            <div className="flex gap-2 mb-1 pb-1 border-b border-gray-700">
              <button
                className="flex-1 px-1.5 py-1 text-[11px] text-red-400 hover:bg-gray-800 rounded flex items-center justify-center gap-1.5"
                onClick={markDone}
              >
                <Check size={10} className="flex-shrink-0" />
                Done (cul-de-sac)
              </button>
              {conn.isTracked && (
                <button
                  className="flex-1 px-1.5 py-1 text-[11px] text-gray-500 hover:bg-gray-800 rounded flex items-center justify-center gap-1.5"
                  onClick={clearEntrance}
                >
                  <X size={10} className="flex-shrink-0" />
                  Clear
                </button>
              )}
            </div>

            <div className="grid grid-cols-2 gap-x-2">
              {/* Left column: Grottos + Interiors */}
              <div>
                <div className="px-1.5 py-0.5 text-[9px] text-gray-500 uppercase tracking-wider">Grotto</div>
                {DEAD_END_GROTTOS.map((d) => (
                  <button key={d.value} className="w-full text-left px-1.5 py-0.5 text-[11px] text-gray-300 hover:bg-gray-800 rounded flex items-center gap-1.5" onClick={() => selectDeadEnd(d.value)}>
                    <span className="w-1.5 h-1.5 rounded-full flex-shrink-0" style={{ backgroundColor: DEAD_END_GROTTO_COLOR }} />
                    {d.label}
                  </button>
                ))}
                <div className="border-t border-gray-700 my-1" />
                <div className="px-1.5 py-0.5 text-[9px] text-gray-500 uppercase tracking-wider">Interior</div>
                {DEAD_END_INTERIORS.map((d) => (
                  <button key={d.value} className="w-full text-left px-1.5 py-0.5 text-[11px] text-gray-300 hover:bg-gray-800 rounded flex items-center gap-1.5" onClick={() => selectDeadEnd(d.value)}>
                    <span className="w-1.5 h-1.5 rounded-full flex-shrink-0" style={{ backgroundColor: DEAD_END_INTERIOR_COLOR }} />
                    {d.label}
                  </button>
                ))}
              </div>

              {/* Right column: Navigation + Bosses */}
              <div>
                <div className="px-1.5 py-0.5 text-[9px] text-gray-500 uppercase tracking-wider">Navigation</div>
                {DEAD_END_NAVIGATION.map((d) => (
                  <button key={d.value} className="w-full text-left px-1.5 py-0.5 text-[11px] text-gray-300 hover:bg-gray-800 rounded flex items-center gap-1.5" onClick={() => selectDeadEnd(d.value)}>
                    <span className="w-1.5 h-1.5 rounded-full flex-shrink-0" style={{ backgroundColor: DEAD_END_NAV_COLOR }} />
                    {d.label}
                  </button>
                ))}
                <div className="border-t border-gray-700 my-1" />
                <div className="px-1.5 py-0.5 text-[9px] text-gray-500 uppercase tracking-wider">Boss</div>
                {DEAD_END_BOSSES.map((d) => (
                  <button key={d.value} className="w-full text-left px-1.5 py-0.5 text-[11px] text-gray-300 hover:bg-gray-800 rounded flex items-center gap-1.5" onClick={() => selectDeadEnd(d.value)}>
                    <span className="w-1.5 h-1.5 rounded-full flex-shrink-0" style={{ backgroundColor: DEAD_END_BOSS_COLOR }} />
                    {d.label}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>,
        document.body,
      )}

      {/* Shop editor modal (double-click on Shop dead end) */}
      {showShopEditor && (
        <ShopEditorModal
          entranceId={conn.id}
          items={shopItems ?? []}
          onSave={onSetShopData}
          onClose={() => setShowShopEditor(false)}
        />
      )}

      {/* Scrubs counter (double-click on Scrubs Grotto dead end) */}
      {showScrubsCounter && pickerPos && (
        <ScrubsCounterModal
          entranceId={conn.id}
          count={scrubsCount}
          onChange={onSetScrubsCount}
          onClose={() => setShowScrubsCounter(false)}
          anchorRect={pickerPos}
        />
      )}
    </div>
  );
}

export const ZoneNode = memo(ZoneNodeComponent);
